import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiGet } from "../../shared/apiClient";
import { useAuth } from "../auth/AuthContext";
import { useToast } from "../../shared/ToastContext";

function SummaryCard({ label, value, tone }) {
  const toneClass =
    tone === "blue"
      ? "from-sky-500 to-indigo-600"
      : tone === "green"
        ? "from-emerald-500 to-green-600"
        : tone === "violet"
          ? "from-violet-500 to-purple-600"
          : "from-slate-600 to-slate-800";

  return (
    <div className={`rounded-xl bg-gradient-to-br ${toneClass} p-4 text-white shadow-sm`}>
      <p className="text-[11px] uppercase tracking-[0.16em] text-white/80">{label}</p>
      <p className="mt-1 text-2xl font-bold tabular-nums">{value}</p>
    </div>
  );
}

function formatDateTime(v) {
  if (!v) return "-";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "-";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short"
  }).format(d);
}

export function WebActivityPage() {
  const { user } = useAuth();
  const toast = useToast();
  const isAdmin = String(user?.role || "").toLowerCase() === "admin";

  const [summary, setSummary] = useState({
    totalVisits: 0,
    todayVisits: 0,
    last7DaysVisits: 0,
    last30DaysVisits: 0,
    uniqueIps: 0,
    uniqueEmails: 0
  });
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [limit, setLimit] = useState(100);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const queryCustomer = useMemo(() => {
    if (!isAdmin || !selectedCustomerId) return "";
    return `customerId=${encodeURIComponent(selectedCustomerId)}`;
  }, [isAdmin, selectedCustomerId]);

  const load = useCallback(
    async (silent = false) => {
      if (silent) setRefreshing(true);
      else setLoading(true);

      const queryParts = [];
      if (queryCustomer) queryParts.push(queryCustomer);
      const rowsQuery = [...queryParts, `limit=${encodeURIComponent(limit)}`].join("&");
      const summaryPath = `/api/WebsiteVisitors/summary${queryParts.length ? `?${queryParts.join("&")}` : ""}`;
      const rowsPath = `/api/WebsiteVisitors${rowsQuery ? `?${rowsQuery}` : ""}`;

      const [summaryRes, rowsRes] = await Promise.all([apiGet(summaryPath), apiGet(rowsPath)]);
      if (!summaryRes.ok) {
        toast.push(String(summaryRes.error || "Failed to load activity summary"), "error");
      } else {
        setSummary({
          totalVisits: summaryRes.data?.totalVisits ?? 0,
          todayVisits: summaryRes.data?.todayVisits ?? 0,
          last7DaysVisits: summaryRes.data?.last7DaysVisits ?? 0,
          last30DaysVisits: summaryRes.data?.last30DaysVisits ?? 0,
          uniqueIps: summaryRes.data?.uniqueIps ?? 0,
          uniqueEmails: summaryRes.data?.uniqueEmails ?? 0
        });
      }

      if (!rowsRes.ok) {
        toast.push(String(rowsRes.error || "Failed to load visitor rows"), "error");
      } else {
        setRows(Array.isArray(rowsRes.data) ? rowsRes.data : []);
      }

      if (silent) setRefreshing(false);
      else setLoading(false);
    },
    [limit, queryCustomer, toast]
  );

  useEffect(() => {
    const loadCustomers = async () => {
      if (!isAdmin) return;
      const res = await apiGet("/api/Customers");
      if (!res.ok) {
        toast.push(String(res.error || "Failed to load customers"), "error");
        return;
      }
      setCustomers(Array.isArray(res.data) ? res.data : []);
    };
    void loadCustomers();
  }, [isAdmin, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 sm:text-2xl">Web Activity</h1>
            <p className="mt-1 text-sm text-slate-500">Site visits, visitor details, and source information.</p>
          </div>
          <button
            type="button"
            onClick={() => void load(true)}
            className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            {refreshing ? "Refreshing..." : "Refresh"}
          </button>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          <SummaryCard label="Total Visits" value={summary.totalVisits} tone="slate" />
          <SummaryCard label="Today" value={summary.todayVisits} tone="blue" />
          <SummaryCard label="Last 7 Days" value={summary.last7DaysVisits} tone="green" />
          <SummaryCard label="Last 30 Days" value={summary.last30DaysVisits} tone="violet" />
          <SummaryCard label="Unique IPs" value={summary.uniqueIps} tone="blue" />
          <SummaryCard label="Unique Emails" value={summary.uniqueEmails} tone="green" />
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-4 py-3 sm:px-5">
          <h2 className="text-sm font-semibold text-slate-800">Visitor table</h2>
          <div className="flex flex-wrap items-center gap-2">
            {isAdmin && (
              <select
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/20"
                value={selectedCustomerId}
                onChange={(e) => setSelectedCustomerId(e.target.value)}
              >
                <option value="">All customers</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name || c.code || `Customer ${c.id}`}
                  </option>
                ))}
              </select>
            )}
            <select
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/20"
              value={String(limit)}
              onChange={(e) => setLimit(Number(e.target.value) || 100)}
            >
              <option value="50">50 rows</option>
              <option value="100">100 rows</option>
              <option value="200">200 rows</option>
              <option value="500">500 rows</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-sm text-slate-500">
            <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-slate-200 border-t-brand-600" />
            Loading...
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-[1600px] w-full text-left text-sm">
              <thead>
                <tr className="bg-white">
                  <th className="whitespace-nowrap px-4 py-3 text-xs font-semibold text-slate-500">Date/Time</th>
                  <th className="whitespace-nowrap px-4 py-3 text-xs font-semibold text-slate-500">Customer</th>
                  <th className="whitespace-nowrap px-4 py-3 text-xs font-semibold text-slate-500">IP</th>
                  <th className="whitespace-nowrap px-4 py-3 text-xs font-semibold text-slate-500">Email</th>
                  <th className="whitespace-nowrap px-4 py-3 text-xs font-semibold text-slate-500">Visitor Name</th>
                  <th className="whitespace-nowrap px-4 py-3 text-xs font-semibold text-slate-500">Device Name</th>
                  <th className="whitespace-nowrap px-4 py-3 text-xs font-semibold text-slate-500">Device Type</th>
                  <th className="whitespace-nowrap px-4 py-3 text-xs font-semibold text-slate-500">Country / Region</th>
                  <th className="whitespace-nowrap px-4 py-3 text-xs font-semibold text-slate-500">Browser / OS</th>
                  <th className="whitespace-nowrap px-4 py-3 text-xs font-semibold text-slate-500">Page URL</th>
                  <th className="whitespace-nowrap px-4 py-3 text-xs font-semibold text-slate-500">Referrer</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50/80">
                    <td className="px-4 py-3 text-slate-700">{formatDateTime(r.visitedAtUtc)}</td>
                    <td className="px-4 py-3 text-slate-700">{r.customerName || r.customerCode || "-"}</td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-700">{r.visitorIp || "-"}</td>
                    <td className="px-4 py-3 text-slate-700">{r.email || "-"}</td>
                    <td className="px-4 py-3 text-slate-700">{r.visitorName || "-"}</td>
                    <td className="px-4 py-3 text-slate-700">{r.deviceName || "-"}</td>
                    <td className="px-4 py-3 text-slate-700">{r.deviceType || "-"}</td>
                    <td className="max-w-[220px] px-4 py-3 text-slate-700">
                      <span
                        className="block truncate"
                        title={[r.country, r.region, r.city].filter(Boolean).join(", ")}
                      >
                        {[r.country, r.region].filter(Boolean).join(" / ") || "-"}
                      </span>
                    </td>
                    <td className="max-w-[260px] px-4 py-3 text-slate-700">
                      <span className="block truncate" title={`${r.browser || "-"} / ${r.operatingSystem || "-"}`}>
                        {(r.browser || "-") + " / " + (r.operatingSystem || "-")}
                      </span>
                    </td>
                    <td className="max-w-[280px] px-4 py-3 text-slate-700">
                      <span className="block truncate" title={r.pageUrl || ""}>
                        {r.pageUrl || "-"}
                      </span>
                    </td>
                    <td className="max-w-[280px] px-4 py-3 text-slate-700">
                      <span className="block truncate" title={r.referrerUrl || ""}>
                        {r.referrerUrl || "-"}
                      </span>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={11} className="px-4 py-12 text-center text-sm text-slate-500">
                      No activity found for current filter.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
